/**
 * Reverse geocoding utility to get location name from coordinates
 */

export const reverseGeocode = async (latitude, longitude, retries = 2) => {
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Attempting reverse geocoding for: ${latitude}, ${longitude} (attempt ${attempt}/${retries})`);
      
      // Using OpenStreetMap Nominatim for reverse geocoding with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=en`,
        { 
          signal: controller.signal,
          headers: {
            'User-Agent': 'WeatherLayersApp/1.0'
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Reverse geocoding response:', data);
      
      if (data && data.display_name) {
        // Extract meaningful parts from the display name
        const parts = data.display_name.split(',').map(part => part.trim());
        
        // Take the first 2-3 most relevant parts (city, region, country)
        let locationParts = [];
        
        if (data.address) {
          // Prefer structured address data
          if (data.address.city) locationParts.push(data.address.city);
          else if (data.address.town) locationParts.push(data.address.town);
          else if (data.address.village) locationParts.push(data.address.village);
          
          if (data.address.state && locationParts.length < 2) locationParts.push(data.address.state);
          if (data.address.country && locationParts.length < 2) locationParts.push(data.address.country);
        } else {
          // Fallback to first few parts of display_name
          locationParts = parts.slice(0, 2);
        }
        
        const result = locationParts.join(', ') || parts[0] || 'Unknown location';
        console.log('Extracted location name:', result);
        return result;
      }
      
      // Fallback to simplified location name
      if (latitude && longitude) {
        // Create a basic location identifier
        const latStr = latitude.toFixed(2);
        const lonStr = longitude.toFixed(2);
        const result = `Location ${latStr}, ${lonStr}`;
        console.log('Using fallback location name:', result);
        return result;
      }
      
      return null;
    } catch (error) {
      console.error(`Reverse geocoding failed (attempt ${attempt}/${retries}):`, error);
      
      if (attempt === retries) {
        console.error('All reverse geocoding attempts failed');
        // Return a fallback location based on coordinates
        if (latitude && longitude) {
          const latStr = latitude.toFixed(2);
          const lonStr = longitude.toFixed(2);
          const fallback = `Location ${latStr}, ${lonStr}`;
          console.log('Using final fallback location:', fallback);
          return fallback;
        }
        return null;
      }
      
      // Exponential backoff: wait 1s, 2s, 4s between retries
      const backoffDelay = Math.pow(2, attempt - 1) * 1000;
      console.log(`Retrying in ${backoffDelay}ms...`);
      await delay(backoffDelay);
    }
  }
  
  return null;
};